/**
 * @fileoverview Shared `beforeinstallprompt` capture hook.
 * @module @firesquirrel/local-db/storage/useInstallPrompt
 *
 * Responsibilities:
 * - Capture and retain the browser-fired `beforeinstallprompt` event so the UI
 *   can offer a deliberate "Install app" affordance later in the session.
 * - Track the resulting install outcome and clear the captured event after a
 *   successful install so the affordance disappears.
 *
 * Dependencies:
 * - React (useState, useEffect, useCallback)
 *
 * Architecture:
 * - This hook exists primarily so that the TetherSync widget can offer an
 *   install path when storage persistence is in best-effort mode. Installing
 *   the PWA is the only reliable way to make Chrome auto-grant
 *   navigator.storage.persist() for an origin that has not yet accumulated
 *   enough site-engagement score on its own.
 * - The hook is intentionally global per app bundle: the listener attaches
 *   on first mount and lives for the lifetime of the page so the captured
 *   event survives later component re-renders.
 *
 * Notes:
 * - Browsers fire `beforeinstallprompt` only when the origin meets PWA install
 *   criteria (HTTPS or localhost, valid manifest, active SW with a fetch
 *   handler). On browsers that never fire it, `prompt()` is null and the
 *   widget hides the install affordance, which is the correct degradation.
 * - `appinstalled` clears the captured prompt because Chrome only fires
 *   `beforeinstallprompt` once per install lifecycle.
 */

import { useCallback, useEffect, useState } from 'react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * @contract BeforeInstallPromptEvent
 *
 * Purpose:
 * - Minimal local copy of the spec-experimental BeforeInstallPromptEvent type
 *   that lib.dom does not yet ship in our TS target.
 *
 * Fields:
 * - prompt(): Triggers the browser's native install prompt UI.
 * - userChoice: Resolves with the user's accept/dismiss decision.
 *
 * Sync:
 * - Local-only browser event; no server interaction.
 */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

/**
 * @contract InstallPromptState
 *
 * Purpose:
 * - Snapshot of the current install-prompt availability and last user choice.
 *
 * Fields:
 * - canInstall: True only when a captured prompt is currently available.
 * - lastOutcome: 'accepted' | 'dismissed' | null — outcome of the most
 *   recently completed prompt, or null if no prompt has been triggered yet.
 * - isInstalled: True once the browser has fired the `appinstalled` event
 *   in this session.
 */
export interface InstallPromptState {
  canInstall: boolean;
  lastOutcome: 'accepted' | 'dismissed' | null;
  isInstalled: boolean;
}

/**
 * @contract UseInstallPromptResult
 *
 * Purpose:
 * - Return shape of useInstallPrompt — combines state with the trigger action.
 *
 * Fields:
 * - state: Current InstallPromptState.
 * - promptInstall: Async action that triggers the browser native install UI.
 *   Resolves with the user's outcome, or null if no prompt is available.
 */
export interface UseInstallPromptResult extends InstallPromptState {
  promptInstall: () => Promise<'accepted' | 'dismissed' | null>;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * @hook useInstallPrompt
 *
 * Purpose:
 * - Capture the browser's `beforeinstallprompt` event so a deliberate install
 *   affordance can be offered from the UI when storage persistence is in
 *   best-effort mode.
 *
 * Returns:
 * - canInstall: true while a captured prompt is available.
 * - lastOutcome: outcome of the most recent prompt, or null.
 * - isInstalled: true once the page receives `appinstalled`.
 * - promptInstall(): triggers the native install prompt and returns the
 *   user's outcome. Returns null if no prompt is currently available.
 *
 * Side Effects:
 * - Adds a `beforeinstallprompt` window listener on mount.
 * - Adds an `appinstalled` window listener on mount.
 * - Calls `event.preventDefault()` on `beforeinstallprompt` so the browser
 *   defers the prompt and we can fire it on user click.
 *
 * Offline Behavior:
 * - No network usage; purely local event capture.
 */
export function useInstallPrompt(): UseInstallPromptResult {
  const [promptEvent, setPromptEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [lastOutcome, setLastOutcome] = useState<'accepted' | 'dismissed' | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    const onBeforeInstallPrompt = (event: Event) => {
      // Stop the browser from showing its mini-infobar; we want to drive
      // the prompt from a user-initiated UI action instead.
      event.preventDefault();
      setPromptEvent(event as BeforeInstallPromptEvent);
    };

    const onAppInstalled = () => {
      setIsInstalled(true);
      setPromptEvent(null);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onAppInstalled);
    };
  }, []);

  const promptInstall = useCallback(async (): Promise<'accepted' | 'dismissed' | null> => {
    if (!promptEvent) return null;
    try {
      await promptEvent.prompt();
      const choice = await promptEvent.userChoice;
      setLastOutcome(choice.outcome);
      // Per spec, the prompt event is one-shot — drop it whether accepted or dismissed.
      setPromptEvent(null);
      return choice.outcome;
    } catch {
      // User dismissed via gesture or browser cancelled — treat as dismissed.
      setLastOutcome('dismissed');
      setPromptEvent(null);
      return 'dismissed';
    }
  }, [promptEvent]);

  return {
    canInstall: promptEvent !== null,
    lastOutcome,
    isInstalled,
    promptInstall,
  };
}
